import React from 'react';
import { Box, Typography, Paper, List, ListItem, ListItemText, Chip, Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Snackbar, Alert, Tabs, Tab, FormControlLabel, Checkbox } from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Block as BlockIcon, LockOpen as UnblockIcon, LockReset as ResetPasswordIcon, VpnKey as SetPasswordIcon } from '@mui/icons-material';
import PageTitle from '../../components/UI/PageTitle';
import ConfirmDialog from '../../components/UI/ConfirmDialog';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import apiClient from '../../api/client';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useAuth } from '../../contexts/AuthContext';

export const UsersPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { selectedOrgId } = useOrganization();
  const { isSuperAdmin, user } = useAuth();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editUser, setEditUser] = React.useState<any>(null);
  const [form, setForm] = React.useState({ username: '', email: '', password: '', firstName: '', lastName: '', role: 'operator', organizationId: '' });
  const [snackbar, setSnackbar] = React.useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [activeTab, setActiveTab] = React.useState(0); // 0 - активные, 1 - заблокированные
  const [resetPasswordDialog, setResetPasswordDialog] = React.useState({ open: false, user: null as any, tempPassword: '' });
  const [setPasswordDialog, setSetPasswordDialog] = React.useState({ open: false, user: null as any, password: '', confirm: '', mustChange: false });

  const { data, isLoading, error } = useQuery(['admin','users', selectedOrgId, activeTab], async () => {
    const params: any = {};
    if (isSuperAdmin && selectedOrgId) {
      params.organizationId = selectedOrgId;
    }
    // Фильтр по статусу: 0 - активные, 1 - заблокированные
    params.status = activeTab === 0 ? 'active' : 'blocked';
    const res = await apiClient.get('/api/admin/users', { params });
    return res.data;
  });

  const createMutation = useMutation((payload: any) => apiClient.post('/api/admin/users', payload), {
    onSuccess: () => { queryClient.invalidateQueries(['admin','users']); setDialogOpen(false); setSnackbar({ open: true, message: 'Пользователь создан', severity: 'success' }); },
    onError: (e: any) => setSnackbar({ open: true, message: e?.response?.data?.message || 'Ошибка создания', severity: 'error' }),
  });
  const updateMutation = useMutation(({ id, payload }: { id: string; payload: any }) => apiClient.put(`/api/admin/users/${id}`, payload), {
    onSuccess: () => { queryClient.invalidateQueries(['admin','users']); setDialogOpen(false); setSnackbar({ open: true, message: 'Пользователь обновлен', severity: 'success' }); },
    onError: (e: any) => setSnackbar({ open: true, message: e?.response?.data?.message || 'Ошибка обновления', severity: 'error' }),
  });
  const toggleActiveMutation = useMutation((id: string) => apiClient.patch(`/api/admin/users/${id}/toggle-active`, {}), {
    onSuccess: () => { queryClient.invalidateQueries(['admin','users']); },
  });  
  const blockMutation = useMutation((id: string) => apiClient.patch(`/api/admin/users/${id}/toggle-active`, {}), {
    onSuccess: () => { 
      queryClient.invalidateQueries(['admin','users']); 
      closeDeleteDialog();
      setSnackbar({ open: true, message: 'Пользователь заблокирован', severity: 'success' }); 
    },
    onError: (e: any) => {
      closeDeleteDialog();
      setSnackbar({ open: true, message: e?.response?.data?.message || 'Ошибка блокировки', severity: 'error' });
    },
  });

  const unblockMutation = useMutation((id: string) => apiClient.patch(`/api/admin/users/${id}/unblock`, {}), {
    onSuccess: () => {
      queryClient.invalidateQueries(['admin','users']);
      setSnackbar({ open: true, message: 'Пользователь разблокирован', severity: 'success' });
    },
    onError: (e: any) => setSnackbar({ open: true, message: e?.response?.data?.message || 'Ошибка разблокировки', severity: 'error' }),
  });

  const resetPasswordMutation = useMutation((id: string) => apiClient.post(`/api/admin/users/${id}/reset-password`, {}), {
    onSuccess: (response) => { 
      queryClient.invalidateQueries(['admin','users']); 
      setResetPasswordDialog({ 
        open: true, 
        user: response.data.data.user, 
        tempPassword: response.data.data.tempPassword 
      });
    },
    onError: (e: any) => setSnackbar({ open: true, message: e?.response?.data?.message || 'Ошибка сброса пароля', severity: 'error' }),
  });

  const setPasswordMutation = useMutation(
    ({ id, payload }: { id: string; payload: { password: string; mustChangePassword: boolean } }) =>
      apiClient.post(`/api/admin/users/${id}/set-password`, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['admin','users']);
        setSetPasswordDialog({ open: false, user: null, password: '', confirm: '', mustChange: false });
        setSnackbar({ open: true, message: 'Пароль пользователя обновлен', severity: 'success' });
      },
      onError: (e: any) => setSnackbar({ open: true, message: e?.response?.data?.message || 'Ошибка установки пароля', severity: 'error' }),
    }
  );

  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [userToDelete, setUserToDelete] = React.useState<any>(null);

  const openCreate = () => { setEditUser(null); setForm({ username: '', email: '', password: '', firstName: '', lastName: '', role: 'operator', organizationId: selectedOrgId || '' }); setDialogOpen(true); };
  const openEdit = (u: any) => { setEditUser(u); setForm({ username: u.username, email: u.email || '', password: '', firstName: u.first_name || '', lastName: u.last_name || '', role: u.role || 'operator', organizationId: u.organizationId || '' }); setDialogOpen(true); };
  const openBlock = (u: any) => { 
    setUserToDelete(u); 
    setDeleteDialogOpen(true); 
  };
  
  const handleBlock = () => { 
    if (userToDelete) blockMutation.mutate(userToDelete.id); 
  };

  const handleUnblock = (user: any) => {
    unblockMutation.mutate(user.id);
  };

  const handleResetPassword = (user: any) => {
    resetPasswordMutation.mutate(user.id);
  };

  const handleOpenSetPassword = (user: any) => {
    setSetPasswordDialog({ open: true, user, password: '', confirm: '', mustChange: false });
  };

  const handleCloseSetPassword = () => {
    setSetPasswordDialog({ open: false, user: null, password: '', confirm: '', mustChange: false });
  };

  const handleSetPasswordSubmit = () => {
    if (!setPasswordDialog.password || setPasswordDialog.password.length < 8) {
      setSnackbar({ open: true, message: 'Пароль должен содержать минимум 8 символов', severity: 'error' });
      return;
    }
    if (setPasswordDialog.password !== setPasswordDialog.confirm) {
      setSnackbar({ open: true, message: 'Пароли не совпадают', severity: 'error' });
      return;
    }
    if (!setPasswordDialog.user) return;

    setPasswordMutation.mutate({
      id: setPasswordDialog.user.id,
      payload: {
        password: setPasswordDialog.password,
        mustChangePassword: setPasswordDialog.mustChange,
      }
    });
  };

  const closeResetPasswordDialog = () => {
    setResetPasswordDialog({ open: false, user: null, tempPassword: '' });
  };
  
  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };
  const handleSubmit = () => {
    const firstName = form.firstName.trim();
    const lastName = form.lastName.trim();
    
    // Проверяем обязательные поля
    if (!firstName) { 
      setSnackbar({ open: true, message: 'Имя обязательно', severity: 'error' }); 
      return; 
    }
    if (!lastName) { 
      setSnackbar({ open: true, message: 'Фамилия обязательна', severity: 'error' }); 
      return; 
    }

    const username = form.username.trim();
    const email = form.email.trim();
    const password = form.password;
    
    // Валидация для не-операторов
    if (form.role !== 'operator' && !editUser) {
      const usernameOk = /^[a-zA-Z0-9_]{3,50}$/.test(username);
      if (!usernameOk) { 
        setSnackbar({ open: true, message: 'Логин: латиница/цифры/_ от 3 символов', severity: 'error' }); 
        return; 
      }
      if (!password || password.length < 8) { 
        setSnackbar({ open: true, message: 'Пароль: минимум 8 символов', severity: 'error' }); 
        return; 
      }
    }

    const payload: any = {
      firstName,
      lastName,
      role: form.role
    };
    
    // Для не-операторов добавляем логин, email и пароль
    if (form.role !== 'operator') {
      payload.username = username;
      payload.email = email || undefined;
      if (!editUser) {
        payload.password = password;
      } else if (editUser && password) {
        payload.password = password;
      }
    } else if (!editUser) {
      // Для операторов при создании backend автогенерирует логин и пароль
      // Не передаем username и password
    }
    
    if (isSuperAdmin && selectedOrgId) payload.organizationId = selectedOrgId;
    
    if (editUser) {
      updateMutation.mutate({ id: editUser.id, payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  return (
    <>
      <PageTitle title="Пользователи - ELEKTROM" />
      
      <Box>
        <Typography variant="h4" component="h1" gutterBottom>
          Управление пользователями
        </Typography>
        
        <Paper sx={{ mt: 2 }}>
          <Tabs 
            value={activeTab} 
            onChange={(e, newValue) => setActiveTab(newValue)}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Активные пользователи" />
            <Tab label="Заблокированные" />
          </Tabs>
          
          <Box sx={{ p: 3 }}>
            {activeTab === 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
                  Добавить пользователя
                </Button>
              </Box>
            )}
          {isLoading && <Typography>Загрузка...</Typography>}
          {error && <Typography color="error">Ошибка загрузки пользователей</Typography>}
          {data && (
            <List>
              {data.data?.users?.length ? data.data.users.map((u: any) => (
                <ListItem key={u.id} divider>
                  <ListItemText 
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>{u.lastName ?? u.last_name ?? ''} {u.firstName ?? u.first_name ?? ''} (@{u.username || 'N/A'})</span>
                        {!(u.isActive ?? u.is_active) && (
                          <Chip 
                            label="Заблокирован" 
                            color="error" 
                            size="small" 
                            variant="outlined"
                          />
                        )}
                      </Box>
                    } 
                    secondary={
                      <React.Fragment>
                        {u.email && <><span>{u.email}</span><br /></>}
                        {u.organizationName && <span>Организация: {u.organizationName}</span>}
                      </React.Fragment>
                    } 
                  />
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Chip 
                      label={u.role === 'operator' ? 'Оператор' : u.role === 'admin' ? 'Администратор' : u.role === 'super_admin' ? 'Супер-админ' : u.role} 
                      color={u.role === 'admin' || u.role === 'super_admin' ? 'primary' : 'default'} 
                      size="small"
                    />
                    {u.mustChangePassword && (
                      <Chip 
                        label="Требует смены пароля" 
                        color="warning" 
                        size="small" 
                      />
                    )}
                    <Chip 
                      clickable 
                      onClick={() => toggleActiveMutation.mutate(u.id)} 
                      label={u.isActive ?? u.is_active ? 'Активен' : 'Неактивен'} 
                      color={u.isActive ?? u.is_active ? 'success' : 'default'} 
                      size="small"
                    />
                    {activeTab === 0 && (
                      <>
                        <IconButton size="small" onClick={() => openEdit(u)}>
                          <EditIcon />
                        </IconButton>
                        {isSuperAdmin && (
                          <IconButton
                            size="small"
                            onClick={() => handleOpenSetPassword(u)}
                            color="primary"
                            disabled={u.role === 'super_admin' || setPasswordMutation.isLoading}
                            title="Установить пароль"
                          >
                            <SetPasswordIcon />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          onClick={() => handleResetPassword(u)}
                          color="info"
                          disabled={u.role === 'super_admin' || u.id === user?.id || resetPasswordMutation.isLoading}
                          title="Сбросить пароль"
                        >
                          <ResetPasswordIcon />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={() => openBlock(u)}
                          color="warning"
                          disabled={u.role === 'super_admin' || u.id === user?.id}
                          title="Заблокировать пользователя"
                        >
                          <BlockIcon />
                        </IconButton>
                      </>
                    )}
                    
                    {activeTab === 1 && (
                      <IconButton 
                        size="small" 
                        onClick={() => handleUnblock(u)}
                        color="success"
                        disabled={unblockMutation.isLoading}
                        title="Разблокировать пользователя"
                      >
                        <UnblockIcon />
                      </IconButton>
                    )}
                  </Box>
                </ListItem>
              )) : <Typography>Пользователи не найдены</Typography>}
            </List>
          )}
          </Box>
        </Paper>

        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{editUser ? 'Редактировать пользователя' : 'Создать пользователя'}</DialogTitle>
          <DialogContent dividers>
            <Box sx={{ display: 'grid', gap: 2, mt: 1 }}>
              <TextField 
                select 
                label="Роль" 
                value={form.role} 
                onChange={(e) => setForm({ ...form, role: e.target.value })} 
                fullWidth
                disabled={!!editUser}
              >
                <MenuItem value="operator">Оператор</MenuItem>
                <MenuItem value="supervisor">Супервайзер</MenuItem>
                <MenuItem value="admin">Администратор</MenuItem>
                <MenuItem value="analyst">Аналитик</MenuItem>
              </TextField>

              <TextField 
                label="Имя" 
                value={form.firstName} 
                onChange={(e) => setForm({ ...form, firstName: e.target.value })} 
                fullWidth 
                required
              />
              
              <TextField 
                label="Фамилия" 
                value={form.lastName} 
                onChange={(e) => setForm({ ...form, lastName: e.target.value })} 
                fullWidth 
                required
              />

              {/* Поля только для не-операторов */}
              {form.role !== 'operator' && (
                <>
                  <TextField 
                    label="Логин" 
                    value={form.username} 
                    onChange={(e) => setForm({ ...form, username: e.target.value })} 
                    fullWidth 
                    disabled={!!editUser}
                    required={!editUser}
                    helperText={!editUser ? "Логин будет использоваться для входа в систему" : undefined}
                  />
                  
                  <TextField 
                    label="Email" 
                    value={form.email} 
                    onChange={(e) => setForm({ ...form, email: e.target.value })} 
                    fullWidth 
                    type="email"
                  />
                  
                  {!editUser && (
                    <TextField 
                      label="Пароль" 
                      type="password" 
                      value={form.password} 
                      onChange={(e) => setForm({ ...form, password: e.target.value })} 
                      fullWidth 
                      required
                      helperText="Минимум 8 символов"
                    />
                  )}
                </>
              )}

              {/* Информация для операторов */}
              {form.role === 'operator' && !editUser && (
                <Alert severity="info">
                  Логин будет создан автоматически из имени и фамилии. 
                  Пароль оператор установит при первом входе в терминальную программу.
                </Alert>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Отмена</Button>
            <Button variant="contained" onClick={handleSubmit} disabled={createMutation.isLoading || updateMutation.isLoading}>
              {editUser ? 'Сохранить' : 'Создать'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Диалог подтверждения блокировки */}
        <ConfirmDialog
          open={deleteDialogOpen}
          title="Подтвердите блокировку"
          onConfirm={handleBlock}
          onCancel={closeDeleteDialog}
          confirmText="Заблокировать"
          cancelText="Отмена"
          confirmColor="warning"
          isLoading={blockMutation.isLoading}
          disabled={
            userToDelete?.role === 'super_admin' || 
            userToDelete?.id === user?.id
          }
        >
          <Typography>
            Вы уверены, что хотите заблокировать пользователя{' '}
            <strong>
              {userToDelete?.firstName || userToDelete?.first_name} {userToDelete?.lastName || userToDelete?.last_name} 
              ({userToDelete?.username})
            </strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            <strong>Внимание:</strong> Пользователь будет заблокирован и перемещен во вкладку "Заблокированные". 
            Вход в систему будет запрещен, но данные сохранятся.
          </Typography>
          <Typography variant="body2" color="info.main" sx={{ mt: 1 }}>
            ℹ️ Пользователя можно будет разблокировать позже.
          </Typography>
          {userToDelete?.role === 'super_admin' && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Невозможно удалить супер-администратора!
            </Alert>
          )}
          {userToDelete?.id === user?.id && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Невозможно удалить собственный аккаунт!
            </Alert>
          )}
        </ConfirmDialog>

        {/* Диалог сброса пароля */}
        <Dialog open={resetPasswordDialog.open} onClose={closeResetPasswordDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Пароль сброшен</DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Пароль для пользователя <strong>{resetPasswordDialog.user?.firstName} {resetPasswordDialog.user?.lastName}</strong> был успешно сброшен.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Новый временный пароль:
            </Typography>
            <Box sx={{ 
              p: 2, 
              bgcolor: 'grey.100', 
              borderRadius: 1, 
              fontFamily: 'monospace',
              fontSize: '1.1rem',
              fontWeight: 'bold',
              textAlign: 'center',
              mb: 2
            }}>
              {resetPasswordDialog.tempPassword}
            </Box>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Важно:</strong> Сообщите этот пароль пользователю. При следующем входе система потребует создать новый пароль.
              </Typography>
            </Alert>
            <Alert severity="info">
              <Typography variant="body2">
                Пользователь сможет войти в систему с этим временным паролем и будет перенаправлен на страницу смены пароля.
              </Typography>
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeResetPasswordDialog} variant="contained">
              Понятно
            </Button>
          </DialogActions>
        </Dialog>

        {/* Диалог установки пароля супер-администратором */}
        <Dialog open={setPasswordDialog.open} onClose={handleCloseSetPassword} maxWidth="sm" fullWidth>
          <DialogTitle>Установить пароль пользователю</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Пользователь: <strong>{setPasswordDialog.user?.lastName || setPasswordDialog.user?.last_name} {setPasswordDialog.user?.firstName || setPasswordDialog.user?.first_name}</strong>
            </Typography>
            <Box sx={{ display: 'grid', gap: 2 }}>
              <TextField
                label="Новый пароль"
                type="password"
                value={setPasswordDialog.password}
                onChange={(e) => setSetPasswordDialog((prev) => ({ ...prev, password: e.target.value }))}
                fullWidth
                required
                helperText="Минимум 8 символов"
              />
              <TextField
                label="Подтвердите пароль"
                type="password"
                value={setPasswordDialog.confirm}
                onChange={(e) => setSetPasswordDialog((prev) => ({ ...prev, confirm: e.target.value }))}
                fullWidth
                required
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={setPasswordDialog.mustChange}
                    onChange={(e) => setSetPasswordDialog((prev) => ({ ...prev, mustChange: e.target.checked }))}
                  />
                }
                label="Потребовать смену пароля при следующем входе"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseSetPassword}>Отмена</Button>
            <Button
              variant="contained"
              onClick={handleSetPasswordSubmit}
              disabled={setPasswordMutation.isLoading}
            >
              {setPasswordMutation.isLoading ? 'Сохранение...' : 'Сохранить'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
        </Snackbar>
      </Box>
    </>
  );
};
